import React from 'react';
import { Link } from 'react-router-dom';
import { Rocket, DollarSign, Check, Clock, ArrowRight, TrendingUp, PieChart, Users, Presentation, Brain, MessageSquare, Eye, Lightbulb } from 'lucide-react';
import MainNavbar from '../components/MainNavbar';
import MainFooter from '../components/MainFooter';

const StartupsUseCase = () => {
  // Benefits data
  const benefits = [
    {
      icon: DollarSign,
      title: "Reduce Initial Costs",
      description: "Create professional-looking prototypes without hiring expensive design resources in your early stages."
    },
    {
      icon: Clock,
      title: "Accelerate Time to Market",
      description: "Move from concept to functional prototype in days instead of weeks or months."
    },
    {
      icon: TrendingUp,
      title: "Validate Before Building",
      description: "Test your product hypothesis with real users before investing in full development."
    },
    {
      icon: Presentation,
      title: "Impress Investors",
      description: "Present interactive prototypes that demonstrate your vision clearly and professionally."
    }
  ];

  // Psychological benefits
  const psychologicalBenefits = [
    {
      icon: Brain,
      title: "Creative Autonomy",
      description: "Experience the confidence of bringing your own product vision to life without depending on external design resources or technical constraints."
    },
    {
      icon: MessageSquare,
      title: "Effortless Idea Communication",
      description: "Eliminate the frustration of misinterpreted ideas by showing exactly what you mean to cofounders, team members, and stakeholders."
    },
    {
      icon: Eye,
      title: "Visualization Clarity",
      description: "Transform abstract concepts into tangible interfaces that help you refine your own thinking and product direction."
    },
    {
      icon: Lightbulb,
      title: "Validation Confidence",
      description: "Gain peace of mind by testing prototypes with real users early, reducing uncertainty about product-market fit before significant investment."
    }
  ];

  // Use case scenarios
  const scenarios = [
    {
      title: "Investor Pitch Preparation",
      description: "Create compelling, interactive demos that showcase your product vision for fundraising meetings."
    },
    {
      title: "MVP Definition & Testing",
      description: "Prototype your minimum viable product to gather user feedback before committing to development."
    },
    {
      title: "Product Market Fit Exploration",
      description: "Quickly test multiple variations of your product to find the optimal market fit."
    },
    {
      title: "Competitive Analysis Visualization",
      description: "Recreate competitor interfaces and explore improvements to differentiate your offering."
    }
  ];

  return (
    <div className="font-sans antialiased text-gray-900">
      {/* Navbar */}
      <MainNavbar />
      
      {/* Hero Section - Changed color scheme to slate */}
      <section className="py-20 bg-gradient-to-r from-slate-800 to-slate-700 text-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl font-bold mb-6">Built for Startup Founders</h1>
              <p className="text-xl mb-8 opacity-90">
                Turn your startup vision into reality with AI-powered prototyping that saves time, reduces costs, and accelerates growth.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-start">
                  <div className="flex-shrink-0 bg-white/20 p-1 rounded-full mt-1">
                    <Check className="h-5 w-5" />
                  </div>
                  <p className="ml-3">Create investor-ready prototypes without design skills</p>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 bg-white/20 p-1 rounded-full mt-1">
                    <Check className="h-5 w-5" />
                  </div>
                  <p className="ml-3">Test and validate your product idea before building</p>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 bg-white/20 p-1 rounded-full mt-1">
                    <Check className="h-5 w-5" />
                  </div>
                  <p className="ml-3">Save on design and development costs in early stages</p>
                </div>
              </div>
              <Link
                to="/signup"
                className="inline-block px-6 py-3 bg-white text-slate-800 rounded-lg hover:bg-gray-100 transition-colors font-medium"
              >
                Start Free Trial
              </Link>
            </div>
            <div className="hidden md:block">
              <div className="bg-white/10 backdrop-blur-sm p-8 rounded-xl shadow-2xl">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                </div>
                <div className="bg-white/10 rounded-md p-4">
                  <div className="flex gap-4 mb-3 items-center">
                    <Rocket className="text-white/70" />
                    <div className="text-sm">Startup Launcher</div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <div className="text-xs text-white/70">MVP Development</div>
                      <div className="text-xs font-semibold text-white">85%</div>
                    </div>
                    <div className="w-full bg-white/10 rounded-full h-2">
                      <div className="bg-slate-300 h-2 rounded-full" style={{ width: "85%" }}></div>
                    </div>
                    
                    <div className="flex justify-between">
                      <div className="text-xs text-white/70">User Feedback</div>
                      <div className="text-xs font-semibold text-white">62%</div>
                    </div>
                    <div className="w-full bg-white/10 rounded-full h-2">
                      <div className="bg-slate-300 h-2 rounded-full" style={{ width: "62%" }}></div>
                    </div>
                    
                    <div className="flex justify-between">
                      <div className="text-xs text-white/70">Investor Ready</div>
                      <div className="text-xs font-semibold text-white">94%</div>
                    </div>
                    <div className="w-full bg-white/10 rounded-full h-2">
                      <div className="bg-slate-300 h-2 rounded-full" style={{ width: "94%" }}></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">How Startup Founders Use Dessino.ai</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From idea to investor-ready prototype without technical skills or expensive resources
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-6">
                <Rocket className="h-8 w-8 text-slate-700" />
              </div>
              <h3 className="text-xl font-bold mb-3">Visualize Your Idea</h3>
              <p className="text-gray-600">
                Transform your startup concept into a visual prototype by simply describing your vision, no technical skills required.
              </p>
            </div>
            
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-6">
                <Users className="h-8 w-8 text-slate-700" />
              </div>
              <h3 className="text-xl font-bold mb-3">Gather Feedback</h3>
              <p className="text-gray-600">
                Test your prototype with real users, collect valuable insights, and iterate quickly based on feedback.
              </p>
            </div>
            
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-6">
                <PieChart className="h-8 w-8 text-slate-700" />
              </div>
              <h3 className="text-xl font-bold mb-3">Pitch With Confidence</h3>
              <p className="text-gray-600">
                Present your startup with interactive prototypes that clearly demonstrate your product vision to investors.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits for Startup Founders */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Benefits for Startup Founders</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Save resources, move faster, and reduce risk in your startup journey
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-sm flex">
                <div className="flex-shrink-0 mr-4">
                  <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                    <benefit.icon className="h-6 w-6 text-slate-700" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Psychological Empowerment - NEW SECTION */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">The Founder's Mindset Advantage</h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Experience the benefits of having complete control over your product's visual expression
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {psychologicalBenefits.map((benefit, index) => (
              <div key={index} className="bg-slate-800 p-6 rounded-lg shadow-md border border-slate-700 flex">
                <div className="flex-shrink-0 mr-4">
                  <div className="w-12 h-12 bg-slate-700 rounded-lg flex items-center justify-center">
                    <benefit.icon className="h-6 w-6 text-slate-300" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2 text-white">{benefit.title}</h3>
                  <p className="text-slate-300">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Founder Experience Comparison - NEW SECTION */}
      <section className="py-20 bg-slate-100">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Transform Your Founder Journey</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Experience the difference in how you ideate, communicate, and validate your startup
            </p>
          </div>
          
          <div className="bg-white p-8 rounded-lg shadow-md">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              <div>
                <h3 className="text-2xl font-bold mb-4 text-slate-800">Without Dessino.ai</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <div className="flex-shrink-0 w-5 h-5 rounded-full bg-red-100 flex items-center justify-center mt-1 mr-3">
                      <span className="text-red-500 text-xs">✕</span>
                    </div>
                    <p className="text-gray-700">Reliance on expensive design agencies or freelancers</p>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 w-5 h-5 rounded-full bg-red-100 flex items-center justify-center mt-1 mr-3">
                      <span className="text-red-500 text-xs">✕</span>
                    </div>
                    <p className="text-gray-700">Weeks of back-and-forth to get designs right</p>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 w-5 h-5 rounded-full bg-red-100 flex items-center justify-center mt-1 mr-3">
                      <span className="text-red-500 text-xs">✕</span>
                    </div>
                    <p className="text-gray-700">Frustration from inability to visualize your exact vision</p>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 w-5 h-5 rounded-full bg-red-100 flex items-center justify-center mt-1 mr-3">
                      <span className="text-red-500 text-xs">✕</span>
                    </div>
                    <p className="text-gray-700">Limited experimentation due to time and budget constraints</p>
                  </li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-2xl font-bold mb-4 text-slate-800">With Dessino.ai</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <div className="flex-shrink-0 w-5 h-5 rounded-full bg-green-100 flex items-center justify-center mt-1 mr-3">
                      <span className="text-green-500 text-xs">✓</span>
                    </div>
                    <p className="text-gray-700">Complete creative control over your product's visual expression</p>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 w-5 h-5 rounded-full bg-green-100 flex items-center justify-center mt-1 mr-3">
                      <span className="text-green-500 text-xs">✓</span>
                    </div>
                    <p className="text-gray-700">Instant visualization that keeps pace with your evolving ideas</p>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 w-5 h-5 rounded-full bg-green-100 flex items-center justify-center mt-1 mr-3">
                      <span className="text-green-500 text-xs">✓</span>
                    </div>
                    <p className="text-gray-700">Early validation with actual users to refine your concept</p>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 w-5 h-5 rounded-full bg-green-100 flex items-center justify-center mt-1 mr-3">
                      <span className="text-green-500 text-xs">✓</span>
                    </div>
                    <p className="text-gray-700">Freedom to explore multiple design directions in parallel</p>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use Case Scenarios */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Practical Use Cases</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See how other founders are leveraging Dessino.ai to grow their startups
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {scenarios.map((scenario, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center mb-3">
                  <Rocket className="w-5 h-5 text-slate-600 mr-2" />
                  <h3 className="text-xl font-bold">{scenario.title}</h3>
                </div>
                <p className="text-gray-600">{scenario.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Startup Journey Section - NEW SECTION */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Streamline Your Startup Journey</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Focus on building your business while we handle the design complexities
            </p>
          </div>
          
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-slate-200"></div>
            
            {/* Timeline items */}
            <div className="space-y-16 relative">
              {/* Item 1 */}
              <div className="flex flex-col md:flex-row items-center">
                <div className="md:w-1/2 md:pr-12 md:text-right mb-6 md:mb-0">
                  <h3 className="text-xl font-bold mb-2">Concept Stage</h3>
                  <p className="text-gray-600">Transform abstract ideas into visual prototypes without design skills</p>
                </div>
                <div className="bg-slate-700 rounded-full w-10 h-10 flex items-center justify-center z-10">
                  <span className="text-white font-bold">1</span>
                </div>
                <div className="md:w-1/2 md:pl-12 mt-6 md:mt-0">
                  <div className="bg-slate-50 p-4 rounded-lg shadow-sm">
                    <p className="text-gray-700 italic">"I was able to see my idea come to life within minutes of describing it."</p>
                  </div>
                </div>
              </div>
              
              {/* Item 2 */}
              <div className="flex flex-col md:flex-row items-center">
                <div className="md:w-1/2 md:pr-12 md:text-right mb-6 md:mb-0 md:order-1">
                  <div className="bg-slate-50 p-4 rounded-lg shadow-sm">
                    <p className="text-gray-700 italic">"Getting early user feedback helped us pivot before investing in development."</p>
                  </div>
                </div>
                <div className="bg-slate-700 rounded-full w-10 h-10 flex items-center justify-center z-10">
                  <span className="text-white font-bold">2</span>
                </div>
                <div className="md:w-1/2 md:pl-12 mt-6 md:mt-0 md:order-0">
                  <h3 className="text-xl font-bold mb-2">Validation Stage</h3>
                  <p className="text-gray-600">Test with real users and gather actionable feedback on your product concept</p>
                </div>
              </div>
              
              {/* Item 3 */}
              <div className="flex flex-col md:flex-row items-center">
                <div className="md:w-1/2 md:pr-12 md:text-right mb-6 md:mb-0">
                  <h3 className="text-xl font-bold mb-2">Funding Stage</h3>
                  <p className="text-gray-600">Present interactive prototypes that clearly communicate your vision to investors</p>
                </div>
                <div className="bg-slate-700 rounded-full w-10 h-10 flex items-center justify-center z-10">
                  <span className="text-white font-bold">3</span>
                </div>
                <div className="md:w-1/2 md:pl-12 mt-6 md:mt-0">
                  <div className="bg-slate-50 p-4 rounded-lg shadow-sm">
                    <p className="text-gray-700 italic">"Investors were impressed by the quality of our prototype despite our early stage."</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-slate-800 to-slate-700 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">Launch Your Startup Faster</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Join founders who use Dessino.ai to bring their ideas to life without design skills or expensive resources.
          </p>
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <Link to="/signup" className="px-8 py-3 bg-white text-slate-800 rounded-lg hover:bg-gray-100 transition-colors font-medium">
              Start Free Trial
            </Link>
          </div>
          <p className="mt-4 text-sm text-white/80">No credit card required. 10 free generations to get you started.</p>
        </div>
      </section>

      {/* Other Use Cases */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Explore Other Use Cases</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Discover how Dessino.ai helps different roles achieve their goals
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Link 
              to="/use-cases/designers"
              className="block bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
            >
              <h3 className="text-xl font-bold mb-3">For UX/UI Designers</h3>
              <p className="text-gray-600 mb-4">
                Generate multiple design options quickly and explore creative alternatives without spending hours on each mockup.
              </p>
              <div className="text-slate-700 font-medium inline-flex items-center">
                Learn more <ArrowRight className="ml-1 w-4 h-4" />
              </div>
            </Link>
            
            <Link 
              to="/use-cases/product-managers"
              className="block bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
            >
              <h3 className="text-xl font-bold mb-3">For Product Managers</h3>
              <p className="text-gray-600 mb-4">
                Visualize features before development begins. Validate ideas with stakeholders using interactive prototypes.
              </p>
              <div className="text-slate-700 font-medium inline-flex items-center">
                Learn more <ArrowRight className="ml-1 w-4 h-4" />
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <MainFooter />
    </div>
  );
};

export default StartupsUseCase;